function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash.debounce';
import { Form } from 'react-bootstrap';
import Dropdown from '../Dropdown';
import { Button, DeleteButton } from '../buttons';
import { multiChoiceFilterValuesFrom, serializeMultiChoiceFilterValues } from './multiChoiceFilterUtils';
const ADD_BUTTON_SIZE = 20;
const ADD_BUTTON_LEFT_SPACING = 5;
const ADD_BUTTON_INPUT_GAP = 8;
const LEFT_BORDER_WIDTH = 1;
const REMOVE_BUTTON_SLOT_WIDTH = 32;
const FILTER_CONTROL_MIN_WIDTH = '200px';
const SUPPORTED_TYPES = ['text', 'select'];
const SELECT_ALL_OPTION = {
  key: 'all',
  value: '',
  text: 'all'
};
const isDE = process.env.PUBLIC_URL === '/de';
const SHOULD_DEFINE_FILTER_MIN_WIDTH = !isDE;
export const MULTI_CHOICE_FILTER_INPUT_OFFSET = `${LEFT_BORDER_WIDTH + ADD_BUTTON_LEFT_SPACING + ADD_BUTTON_SIZE + ADD_BUTTON_INPUT_GAP}px`;
export const MULTI_CHOICE_FILTER_CONTROL_MIN_WIDTH = FILTER_CONTROL_MIN_WIDTH;

/**
 * Makes sure there is always at least one empty string - for which we render an input field.
 * Otherwise we would render no input fields for that filter.
 * @param {*} value
 * @returns
 */
const inputValuesFrom = value => {
  const inputValues = multiChoiceFilterValuesFrom(value);

  // Empty filter values still need one blank row so the component renders an input.
  return inputValues.length > 0 ? inputValues : [''];
};
const optionValue = option => typeof option === 'object' && option !== null ? option.value : option;
const normalizeSelectOption = option => typeof option === 'object' && option !== null ? option : {
  key: option,
  value: option,
  text: option
};
const selectOptionsWithAllOption = options => [SELECT_ALL_OPTION, ...(options || []).filter(option => optionValue(option) !== '').map(normalizeSelectOption)];
export default function MultiChoiceFilter(_ref) {
  let {
    type = 'text',
    value,
    initialValue = '',
    onChange,
    onFilter,
    placeholder = 'Filter',
    className = '',
    inputClassName = '',
    inputSize,
    inputStyle,
    options = [],
    dataTestId,
    dropdownStyle = {
      width: '100%'
    },
    showPlusButton = true,
    style,
    ...props
  } = _ref;
  if (!SUPPORTED_TYPES.includes(type)) {
    throw new Error(`Unsupported MultiChoiceFilter type: ${type}`);
  }
  const filterValue = value === undefined ? initialValue : value;
  const [inputValues, setInputValues] = useState(inputValuesFrom(filterValue));
  const shouldShowLeftBorder = inputValues.length > 1;
  const plusButtonLeftSpacing = showPlusButton ? ADD_BUTTON_LEFT_SPACING : 0;
  const plusButtonInputGap = showPlusButton ? ADD_BUTTON_INPUT_GAP : 0;
  const plusButtonColumnWidth = showPlusButton ? ADD_BUTTON_SIZE : 0;
  const debouncedOnFilter = useMemo(() => {
    if (!onFilter) return null;
    return debounce(nextValue => onFilter(nextValue), 500);
  }, [onFilter]);
  useEffect(() => {
    return () => {
      if (debouncedOnFilter) {
        debouncedOnFilter.cancel();
      }
    };
  }, [debouncedOnFilter]);
  useEffect(() => {
    setInputValues(currentInputValues => {
      const currentSerializedValue = serializeMultiChoiceFilterValues(currentInputValues);
      const nextInputValues = inputValuesFrom(filterValue);
      const nextSerializedValue = serializeMultiChoiceFilterValues(nextInputValues);
      if (currentSerializedValue === nextSerializedValue) {
        return currentInputValues;
      }
      return nextInputValues;
    });
  }, [filterValue]);
  const notifyFilterChange = updatedInputValues => {
    const serializedValue = serializeMultiChoiceFilterValues(updatedInputValues);
    if (onChange) {
      onChange(serializedValue);
    }
    if (debouncedOnFilter) {
      debouncedOnFilter(serializedValue);
    }
  };
  const updateInputValue = (index, nextValue) => {
    if (type === 'select' && nextValue === '') {
      setInputValues(['']);
      notifyFilterChange(['']);
      return;
    }
    const updatedInputValues = inputValues.map((inputValue, inputValueIndex) => inputValueIndex === index ? nextValue : inputValue);
    setInputValues(updatedInputValues);
    notifyFilterChange(updatedInputValues);
  };
  const addInputValueAfter = index => {
    const updatedInputValues = [...inputValues];
    updatedInputValues.splice(index + 1, 0, '');
    setInputValues(updatedInputValues);
  };
  const removeInputValue = index => {
    const updatedInputValues = inputValues.filter((_, inputValueIndex) => inputValueIndex !== index);
    const nextInputValues = updatedInputValues.length > 0 ? updatedInputValues : [''];
    setInputValues(nextInputValues);
    notifyFilterChange(nextInputValues);
  };
  const stopClickPropagation = event => {
    event.preventDefault();
    event.stopPropagation();
  };
  const stopPropagation = event => {
    event.stopPropagation();
  };
  const addInputValue = (event, index) => {
    stopClickPropagation(event);
    addInputValueAfter(index);
  };
  const deleteInputValue = (event, index) => {
    stopClickPropagation(event);
    removeInputValue(index);
  };
  const dataTestIdFor = index => {
    if (!dataTestId) return undefined;
    return index === 0 ? dataTestId : `${dataTestId}-${index}`;
  };
  const addButtonDataTestIdFor = index => {
    if (!dataTestId) return undefined;
    return `${dataTestId}-add-${index}`;
  };
  const removeButtonDataTestIdFor = index => {
    if (!dataTestId) return undefined;
    return `${dataTestId}-remove-${index}`;
  };
  const renderControl = (inputValue, index) => {
    const appliedDropdownStyle = {
      ...dropdownStyle,
      ...(SHOULD_DEFINE_FILTER_MIN_WIDTH ? {
        minWidth: FILTER_CONTROL_MIN_WIDTH
      } : {})
    };
    const appliedInputStyle = {
      ...inputStyle,
      ...(SHOULD_DEFINE_FILTER_MIN_WIDTH ? {
        minWidth: FILTER_CONTROL_MIN_WIDTH
      } : {})
    };
    if (type === 'select') {
      return /*#__PURE__*/React.createElement(Dropdown, {
        dataTestId: dataTestIdFor(index),
        dropdownStyle: appliedDropdownStyle,
        onChange: nextValue => updateInputValue(index, nextValue),
        onClick: stopPropagation,
        options: selectOptionsWithAllOption(options),
        showNullOption: false,
        value: inputValue || ''
      });
    }
    return /*#__PURE__*/React.createElement(Form.Control, {
      className: inputClassName,
      "data-testid": dataTestIdFor(index),
      onChange: event => updateInputValue(index, event.target.value),
      onClick: stopClickPropagation,
      placeholder: placeholder,
      size: inputSize,
      style: appliedInputStyle,
      value: inputValue || ''
    });
  };
  const renderRemoveButtonSlot = index => {
    return /*#__PURE__*/React.createElement("span", {
      style: {
        minWidth: `${REMOVE_BUTTON_SLOT_WIDTH}px`,
        width: `${REMOVE_BUTTON_SLOT_WIDTH}px`
      }
    }, inputValues.length > 1 && /*#__PURE__*/React.createElement(DeleteButton, {
      "aria-label": "Remove OR filter value",
      "data-testid": removeButtonDataTestIdFor(index),
      onClick: event => deleteInputValue(event, index),
      type: "button"
    }));
  };
  return /*#__PURE__*/React.createElement("div", _extends({
    className: `MultiChoiceFilter d-flex flex-column gap-2 ${className}`,
    style: {
      ...style,
      borderLeft: `${LEFT_BORDER_WIDTH}px solid ${shouldShowLeftBorder ? 'black' : 'transparent'}`,
      paddingLeft: `${plusButtonLeftSpacing}px`
    }
  }, props), inputValues.map((inputValue, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: `multi-choice-filter-${index}`
  }, index > 0 && /*#__PURE__*/React.createElement("div", {
    className: "text-muted font-weight-bold small",
    style: {
      width: `${plusButtonColumnWidth}px`,
      textAlign: 'center'
    }
  }, "OR"), /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center",
    style: {
      gap: `${plusButtonInputGap}px`
    }
  }, showPlusButton && /*#__PURE__*/React.createElement(Button, {
    "aria-label": "Add OR filter value",
    className: "p-0 d-inline-flex align-items-center justify-content-center rounded-circle",
    "data-testid": addButtonDataTestIdFor(index),
    icon: "add",
    onClick: event => addInputValue(event, index),
    style: {
      height: `${ADD_BUTTON_SIZE}px`,
      minWidth: `${ADD_BUTTON_SIZE}px`,
      width: `${ADD_BUTTON_SIZE}px`
    },
    type: "button",
    variant: "light"
  }), renderControl(inputValue, index), showPlusButton && renderRemoveButtonSlot(index)))));
}